import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { motion, type Variants } from 'framer-motion';

import { observeElementResize } from '#/lib/observe-element-resize';
import type { SanityTableSection } from '#/lib/sanity/queries/tableSection';
import {
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { formatHeadline } from '#/components/Headline';

const REVEAL_VIEWPORT = { once: true, margin: '-150px' } as const;

const introGroup: Variants = {
  initial: {},
  animate: { transition: { delayChildren: 0.1, staggerChildren: 0.1 } },
};

const fadeUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// The right-edge fade hints there's more table to scroll to on mobile. It has
// to match the section surface so it reads as the background bleeding over.
const SCROLL_FADE_CLASSES: Record<SanityTableSection['theme'], string> = {
  light: 'from-grayscale-01',
  charcoal: 'from-charcoal',
  purple: 'from-cipher',
};

export const TableSection: FC<SanityTableSection> = (props) => {
  const isDark = isDarkSectionTheme(props.theme);
  const accentClass = getSectionThemeAccent(props.theme);
  const surfaceClass = getSectionThemeSurface(props.theme);

  const rows = props.rows ?? [];
  const hasRowHeaders = rows.some((row) => row.header);
  const columnHeaders = props.columnHeaders ?? [];

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(false);

  const updateScrollFade = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    setShowScrollFade(maxScroll > 1 && scroller.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateScrollFade();
    scroller.addEventListener('scroll', updateScrollFade, { passive: true });
    const disconnect = observeElementResize(scroller, updateScrollFade);

    return () => {
      scroller.removeEventListener('scroll', updateScrollFade);
      disconnect();
    };
  }, [updateScrollFade]);

  const borderClass = isDark ? 'border-grayscale-05' : 'border-grayscale-03';
  const cellTextClass = isDark ? 'text-grayscale-02' : 'text-charcoal';
  // The pinned column needs an opaque background so table content slides
  // underneath it while side-scrolling on mobile.
  const rowHeaderClasses = cn(
    'sticky left-0 z-1 w-142 min-w-142 border-b p-10 py-18 text-left align-top m:w-266 m:min-w-266',
    borderClass,
    surfaceClass,
  );
  const dataCellWidthClasses = 'max-m:min-w-220';

  return (
    <div className={cn('p-main py-40 m:py-60 l:py-100', surfaceClass)}>
      <div className="mx-auto flex max-w-1235 flex-col gap-40 m:gap-90">
        {(props.eyebrow || props.title) && (
          <motion.div
            className="flex max-w-800 flex-col gap-24 m:gap-48"
            variants={introGroup}
            initial="initial"
            whileInView="animate"
            viewport={REVEAL_VIEWPORT}
          >
            {props.eyebrow ? (
              <motion.p
                variants={fadeUp}
                className={cn('type-eyebrow', accentClass)}
              >
                {props.eyebrow}
              </motion.p>
            ) : null}
            {props.title ? (
              <motion.h2
                variants={fadeUp}
                className="type-heading5 m:type-heading3 whitespace-pre-line"
              >
                {formatHeadline(props.title, accentClass)}
              </motion.h2>
            ) : null}
          </motion.div>
        )}

        <div className="relative">
          <div ref={scrollerRef} className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              {columnHeaders.length > 0 && (
                <thead>
                  <tr>
                    {hasRowHeaders && <th className={rowHeaderClasses} />}
                    {columnHeaders.map((header, index) => (
                      <th
                        key={index}
                        className={cn(
                          'border-b p-10 py-18 text-left align-top font-normal',
                          borderClass,
                          dataCellWidthClasses,
                        )}
                      >
                        <span
                          className={cn(
                            'type-heading6 m:type-heading5',
                            cellTextClass,
                          )}
                        >
                          {header}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.map((row) => (
                  <tr key={row._key}>
                    {hasRowHeaders && (
                      <th scope="row" className={rowHeaderClasses}>
                        <span className="text-14 font-medium leading-150 m:text-18">
                          {row.header}
                        </span>
                      </th>
                    )}
                    {(row.cells ?? []).map((cell) => (
                      <td
                        key={cell._key}
                        className={cn(
                          'border-b p-10 py-18 align-top',
                          borderClass,
                          dataCellWidthClasses,
                        )}
                      >
                        {cell.content ? (
                          <BasicText
                            value={cell.content}
                            className={cn(
                              'type-paragraph-sm m:type-paragraph [&_a]:underline',
                              cellTextClass,
                            )}
                          />
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-0 right-0 w-80 bg-gradient-to-l to-transparent transition-opacity duration-300 m:hidden',
              SCROLL_FADE_CLASSES[props.theme],
              showScrollFade ? 'opacity-100' : 'opacity-0',
            )}
          />
        </div>
      </div>
    </div>
  );
};
